import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LayoutGrid, Link2, List, Search, Plus, Users, TrendingUp, MessageSquare, Trophy, Trash2, Loader2, AlertTriangle, CheckSquare, Tag, X, Send, Megaphone } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { PageHeader } from '@/components/layout/page-header'
import { FieldHint } from '@/components/field-hint'
import { SectionHelp } from '@/components/help/section-help'
import { cn } from '@/lib/utils'
import { AnimatedNumber } from '@/components/animated-number'
import { KanbanBoard } from './kanban-board'
import { LeadsTable } from './leads-table'
import { LeadDetail } from './lead-detail'
import { AddLeadDialog } from './add-lead-dialog'
import { AddToCampaignDialog } from './add-to-campaign-dialog'
import { useLeads, useLeadStats, useDeleteAllLeads, useBulkDeleteLeads, useUpdateLead } from './use-leads'
import { useCampaigns } from '@/features/campaigns/use-campaigns'
import { KANBAN_COLUMNS, type Lead, type LeadStatus, type ViewMode } from './types'

const VIEW_MODE_KEY = 'leads_view_mode'

type StatCardProps = {
  readonly label: string
  readonly value: string | number
  readonly icon: React.ComponentType<{ className?: string }>
  readonly trend?: string
  readonly hint?: string
}

function StatCard({ label, value, icon: Icon, trend, hint }: StatCardProps) {
  // Render numbers via AnimatedNumber (drop-in + tween); pass-through strings
  // like percentages ("12%") or em-dashes without animating.
  const isNumeric = typeof value === 'number'
  return (
    <Card className="group relative flex-1 overflow-hidden">
      <div
      aria-hidden="true"
      className="pointer-events-none absolute -right-10 -top-10 h-24 w-24 rounded-full bg-[var(--color-cobalt)]/0 blur-2xl transition-all duration-300 group-hover:bg-[var(--color-cobalt)]/15"
    />
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <p className="eyebrow text-[var(--color-silver)]">{label}</p>
            {hint && <FieldHint label={`What is ${label}?`}>{hint}</FieldHint>}
          </div>
          <Icon className="h-4 w-4 text-[var(--color-cobalt-bright)]" />
        </div>
        <div className="mt-1 overflow-clip-y text-2xl font-bold tracking-tight text-[var(--color-silver-bright)]">
          {isNumeric ? (
              <AnimatedNumber
              value={value}
              format={(n) => Math.round(n).toLocaleString()}
              className="inline-block animate-digit-drop"
            />
          ) : (
            <span className="inline-block animate-digit-drop">{value}</span>
          )}
        </div>
        {trend && <p className="mt-0.5 text-xs text-[var(--color-silver)]">{trend}</p>}
      </CardContent>
    </Card>
  )
}

export function LeadsPage() {
  const navigate = useNavigate()
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try {
      const saved = localStorage.getItem(VIEW_MODE_KEY)
      return (saved as ViewMode | null) ?? 'kanban'
    } catch {
      return 'kanban'
    }
  })
  const [search, setSearch] = useState('')
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [addLeadOpen, setAddLeadOpen] = useState(false)
  const [deleteAllOpen, setDeleteAllOpen] = useState(false)
  const [confirmText, setConfirmText] = useState('')

  // Selection mode — when active, lead clicks toggle a checkbox instead of
  // opening the detail panel. Set is the source of truth (cheap membership
  // checks for both kanban cards and table rows).
  const [selectMode, setSelectMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())
  const [addCampaignOpen, setAddCampaignOpen] = useState(false)
  // Campaign filter — 'all' shows the whole pipeline; a campaign id narrows
  // the board/table server-side (the API filters on campaign_id).
  const [campaignFilter, setCampaignFilter] = useState<string>('all')

  const { data: leads = [], isLoading: leadsLoading } = useLeads({
    search: search || undefined,
    campaign: campaignFilter !== 'all' ? campaignFilter : undefined,
  })
  const { data: stats } = useLeadStats()
  const { data: allCampaigns = [] } = useCampaigns()
  const filterableCampaigns = allCampaigns.filter((c) => c.status !== 'deleted')
  const deleteAll = useDeleteAllLeads()
  const bulkDelete = useBulkDeleteLeads()
  const updateLead = useUpdateLead()

  // Drop selections that no longer correspond to a fetched lead — happens
  // after a delete or after the list re-fetches with a search filter applied.
  const visibleIds = useMemo(() => new Set(leads.map((l) => l.id)), [leads])
  const effectiveSelected = useMemo(
    () => new Set([...selectedIds].filter((id) => visibleIds.has(id))),
    [selectedIds, visibleIds],
  )

  function toggleSelectMode() {
    setSelectMode((m) => {
      if (m) setSelectedIds(new Set())
      return !m
    })
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function selectAllVisible() {
    setSelectedIds(new Set(leads.map((l) => l.id)))
  }

  function clearSelection() {
    setSelectedIds(new Set())
  }

  async function handleBulkDelete() {
    const ids = [...effectiveSelected]
    if (ids.length === 0) return
    await bulkDelete.mutateAsync(ids).catch(() => undefined)
    setSelectedIds(new Set())
  }

  function handleBulkStatusChange(status: LeadStatus) {
    const ids = [...effectiveSelected]
    if (ids.length === 0) return
    for (const id of ids) {
      updateLead.mutate({ id, updates: { status } })
    }
    setSelectedIds(new Set())
  }

  async function handleConfirmDeleteAll() {
    try {
      await deleteAll.mutateAsync()
      setDeleteAllOpen(false)
      setConfirmText('')
    } catch { /* error surfaced in dialog */ }
  }

  function changeViewMode(mode: ViewMode) {
    setViewMode(mode)
    try {
      localStorage.setItem(VIEW_MODE_KEY, mode)
    } catch {
      // localStorage not available
    }
  }

  function handleLeadClick(lead: Lead) {
    // In select mode, the click toggles selection instead of opening detail.
    if (selectMode) {
      toggleSelect(lead.id)
      return
    }
    setSelectedLead(lead)
    setDetailOpen(true)
  }

  function handleDetailClose(open: boolean) {
    setDetailOpen(open)
    if (!open) {
      // Small delay before clearing lead so exit animation completes
      setTimeout(() => setSelectedLead(null), 300)
    }
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        title="Lead Pipeline"
        subtitle="Manage and track your leads through the pipeline."
        actions={
          <div className="flex items-center gap-2">
            <SectionHelp sectionKey="leads" />
            {/* Select toggle — only useful when there's something to select. */}
            {leads.length > 0 && (
              <Button
                size="sm"
                variant={selectMode ? 'default' : 'outline'}
                className="text-xs"
                onClick={toggleSelectMode}
                data-tour="leads-select"
              >
                {selectMode ? <X className="h-3.5 w-3.5" /> : <CheckSquare className="h-3.5 w-3.5" />}
                {selectMode ? 'Exit select' : 'Select'}
              </Button>
            )}
            {/* Delete-all — only offered when there's actually something to delete. */}
            {(stats?.total ?? 0) > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="text-xs text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                onClick={() => setDeleteAllOpen(true)}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete all
              </Button>
            )}
            <Button size="sm" variant="outline" className="text-xs" onClick={() => setAddLeadOpen(true)}>
              <Link2 className="h-3.5 w-3.5" />
              Add with URL
            </Button>
            <Button
              size="sm"
              className="text-xs"
              onClick={() => navigate('/leads/discover')}
              data-tour="leads-add"
            >
              <Plus className="h-3.5 w-3.5" />
              Add Lead
            </Button>
          </div>
        }
      />

      {/* Bulk action bar — visible whenever select mode is on, so the user
          can see "0 selected" and the Select-all affordance immediately
          rather than after their first click. */}
      {selectMode && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[var(--color-cobalt)]/30 bg-[var(--color-cobalt)]/5 px-4 py-2">
          <span className="text-sm font-medium">
            {effectiveSelected.size} selected
          </span>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 text-xs"
            onClick={selectAllVisible}
            disabled={leads.length === 0 || effectiveSelected.size === leads.length}
          >
            Select all ({leads.length})
          </Button>
          {effectiveSelected.size > 0 && (
            <>
              <Button
                size="sm"
                className="h-8 text-xs"
                onClick={() => setAddCampaignOpen(true)}
              >
                <Send className="h-3 w-3" />
                Add to Campaign
              </Button>
              <Select onValueChange={(v) => handleBulkStatusChange(v as LeadStatus)}>
                <SelectTrigger className="h-8 w-44 text-xs">
                  <div className="flex items-center gap-1.5">
                    <Tag className="h-3 w-3" />
                    <SelectValue placeholder="Change status" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {KANBAN_COLUMNS.map((col) => (
                    <SelectItem key={col.id} value={col.id} className="text-xs">
                      {col.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="destructive"
                className="h-8 text-xs"
                onClick={handleBulkDelete}
                disabled={bulkDelete.isPending}
              >
                {bulkDelete.isPending ? (
                  <><Loader2 className="h-3 w-3 animate-spin" /> Deleting…</>
                ) : (
                  <><Trash2 className="h-3 w-3" /> Delete {effectiveSelected.size}</>
                )}
              </Button>
              <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={clearSelection}>
                Clear
              </Button>
            </>
          )}
        </div>
      )}

      {/* Stats row */}
      <div className="flex flex-col gap-3 sm:flex-row" data-tour="leads-stats">
        <StatCard
          label="Total Leads"
          value={stats?.total ?? 0}
          icon={Users}
          trend={stats ? `${stats.by_status.won} won` : undefined}
          hint="Every lead in your pipeline across all stages and campaigns."
        />
        <StatCard
          label="This Week"
          value={stats?.this_week ?? 0}
          icon={TrendingUp}
          trend="New leads added"
          hint="Leads added in the last 7 days."
        />
        <StatCard
          label="Reply Rate"
          value={stats ? `${stats.reply_rate}%` : '-'}
          icon={MessageSquare}
          trend="Messages with replies"
          hint="The share of contacted leads who have replied at least once."
        />
        <StatCard
          label="Win Rate"
          value={stats ? `${stats.win_rate}%` : '-'}
          icon={Trophy}
          trend="Qualified to won"
          hint="The share of qualified leads you have marked Won."
        />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        {/* View toggle */}
        <div className="flex rounded-md border p-0.5" data-tour="leads-view-toggle">
          <button
            type="button"
            onClick={() => changeViewMode('kanban')}
            className={cn(
              'flex items-center gap-1.5 rounded px-2.5 py-1.5 text-xs transition-colors',
              viewMode === 'kanban'
                ? 'bg-background shadow-sm text-foreground font-medium'
                : 'text-muted-foreground hover:text-foreground',
            )}
            aria-label="Kanban view"
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            Kanban
          </button>
          <button
            type="button"
            onClick={() => changeViewMode('table')}
            className={cn(
              'flex items-center gap-1.5 rounded px-2.5 py-1.5 text-xs transition-colors',
              viewMode === 'table'
                ? 'bg-background shadow-sm text-foreground font-medium'
                : 'text-muted-foreground hover:text-foreground',
            )}
            aria-label="Table view"
          >
            <List className="h-3.5 w-3.5" />
            Table
          </button>
        </div>

        {/* Search */}
        <div className="relative flex-1 max-w-xs" data-tour="leads-filters">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search leads..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 pl-8 text-sm"
          />
        </div>

        {/* Campaign filter */}
        {filterableCampaigns.length > 0 && (
          <Select value={campaignFilter} onValueChange={setCampaignFilter}>
            <SelectTrigger className="h-8 w-56 text-xs">
              <div className="flex items-center gap-1.5">
                <Megaphone className="h-3 w-3" />
                <SelectValue placeholder="All campaigns" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">
                All campaigns
              </SelectItem>
              {filterableCampaigns.map((c) => (
                <SelectItem key={c.id} value={c.id} className="text-xs">
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Main content */}
      <div data-tour="leads-board">
        {leadsLoading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
            Loading leads...
          </div>
        ) : viewMode === 'kanban' ? (
          <KanbanBoard
            leads={leads}
            onLeadClick={handleLeadClick}
            selectMode={selectMode}
            selectedIds={effectiveSelected}
            onToggleSelect={toggleSelect}
          />
        ) : (
          <LeadsTable
            leads={leads}
            onLeadClick={handleLeadClick}
            selectMode={selectMode}
            selectedIds={effectiveSelected}
            onToggleSelect={toggleSelect}
          />
        )}
      </div>

      {/* Lead detail slide-over */}
      <LeadDetail
        lead={selectedLead}
        open={detailOpen}
        onOpenChange={handleDetailClose}
      />
      <AddLeadDialog
        open={addLeadOpen}
        onOpenChange={setAddLeadOpen}
        onLeadCreated={(lead) => {
          setSelectedLead(lead)
          setDetailOpen(true)
        }}
      />

      {/* Bulk "Add to Campaign" for the current selection. Clearing the
          selection on success keeps the bulk bar honest (the leads are now
          assigned; nothing left to act on). */}
      <AddToCampaignDialog
        leadIds={[...effectiveSelected]}
        leads={leads}
        open={addCampaignOpen}
        onOpenChange={setAddCampaignOpen}
        onAdded={() => setSelectedIds(new Set())}
      />

      {/* Delete-all confirmation. Requires typing the word DELETE so a
          misclick can't wipe the pipeline. Running while disabled is
          physically impossible — both the button and the input are
          locked while the mutation is in flight. */}
      <Dialog
        open={deleteAllOpen}
        onOpenChange={(o) => {
          if (deleteAll.isPending) return
          setDeleteAllOpen(o)
          if (!o) setConfirmText('')
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <DialogTitle>Delete ALL leads?</DialogTitle>
            </div>
            <DialogDescription className="pt-2 text-foreground/90">
              This permanently removes{' '}
              <span className="font-semibold">
                {stats?.total ?? 0} lead{(stats?.total ?? 0) === 1 ? '' : 's'}
              </span>
              {' '}along with their conversation history, signals, and campaign
              assignments. <strong>This cannot be undone.</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <label className="text-xs text-muted-foreground">
              Type <span className="font-mono font-semibold">DELETE</span> to confirm:
            </label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              disabled={deleteAll.isPending}
              autoFocus
            />
            {deleteAll.error && (
              <p className="text-xs text-destructive">
                {deleteAll.error instanceof Error ? deleteAll.error.message : 'Failed to delete'}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteAllOpen(false)
                setConfirmText('')
              }}
              disabled={deleteAll.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDeleteAll}
              disabled={deleteAll.isPending || confirmText !== 'DELETE'}
            >
              {deleteAll.isPending ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Deleting…</>
              ) : (
                <><Trash2 className="h-4 w-4 mr-1" /> Delete {stats?.total ?? 0} leads</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
